//@ts-ignore
import {PitchShifter} from 'soundtouchjs';
import {logger} from "./log/logger";
//@ts-ignore
const AudioContext = window.AudioContext || window.webkitAudioContext;
const latencyHint = 0; //"interactive"; //the preferred maximum latency in seconds for the audio context.

function createAudioContext(): AudioContext {
  return new AudioContext({latencyHint, });
}

const audioContext = createAudioContext();

export function getAudioContext(): AudioContext {
  return audioContext;
}

const scratchBuffer = audioContext.createBuffer(1,1,22050);
export function getScratchBuffer(): AudioBuffer {
  return scratchBuffer;
}

/**
 * Given an array length, and the desired number of balanced chunks the array should be broken into, return an array of arrays, where each array has 2 elements: the inclusive
 * start and end index.  e.g. array of [1, 2, 3, 4] into 3 parts should result in an array of 3 arrays: [ [0, 1], [2, 2], [3, 3] ]
 * https://codepen.io/jasonmcaffee/pen/bGgRdGe
 */
export function calculateChunkIndexRanges(arrayLength: number, chunks: number): [number[]] {
  if(arrayLength < chunks){ throw new Error(`array length must at least be the size of the number of chunks `); }
  //an array of arrays, where the array entries are referred to as chunks. e.g. [ chunk, chunk, chunk ];
  //@ts-ignore
  const result: [number[]] = [];
  //a chunk will have a range of indexes that it covers via start and end. e.g. [0, 4] would be 5 indexes, meaning the chunk size is 5.
  //It is useful to think of the result in terms of chunk sizes. e.g arr.length of 7 would result in chunk sizes [3, 2, 2]
  const biggestChunkSize = Math.ceil(arrayLength / chunks);
  //Next we want to find the index in our result where our biggestChunkSize stops fitting.  Modulus works well for this when creating a balanced result.
  //arr of [1, 2, 3, 4] with 3 parts would get grouped into 3 chunks with sizes [2, 1, 1], in which case there is 1 chunk with the biggest chunk size of 2.
  const numberOfChunksWithBiggestChunkSize = arrayLength % chunks; //e.g. [1, 2, 3, 4] with 3 parts = [2, 1, 1] = 1 == 4 % 3
  //how many chunks are there after removing the number of chunks with biggest chunks size.
  const remainingChunks = chunks - numberOfChunksWithBiggestChunkSize;
  //now we want to find the size of our remaining chunks.
  //e.g. arr [1, 2, 3, 4] with 3 parts = [2, 1, 1] has remaining chunk size of 1. arr [1, 2, 3, 4, 5, 6, 7] = [3, 2, 2] has remaining chunk size 2
  //since we used modulus, we know that the remaining chunks will always be of equal size.
  //find the total size of our biggest chunks.
  const biggestChunksTotalSize = numberOfChunksWithBiggestChunkSize * biggestChunkSize;
  //determine the total size we have remaining
  const remainingChunksTotalSize = arrayLength - biggestChunksTotalSize;
  //evenly divide up the remaining total size to find the smallest chunk size
  const smallestChunkSize = remainingChunksTotalSize / remainingChunks;
  //now determine which index we should switch between using biggestChunkSize and smallestChunkSize
  const areAllChunksOfEqualSize = arrayLength % chunks === 0;
  const smallestChunksBeginAtIndex = areAllChunksOfEqualSize ? chunks : numberOfChunksWithBiggestChunkSize;

  let start = 0;
  for(let i = 0; i < chunks; ++i){
    const chunkSize = i < smallestChunksBeginAtIndex ? biggestChunkSize : smallestChunkSize;
    const end = start + chunkSize - 1;
    result.push([start, end]);
    start = end + 1;
  }

  return result;
}

/**
 * Some iOS versions have a memory leak which this allegedely fixes.
 * @param source
 */
export function cleanupSource(source: AudioBufferSourceNode | undefined): void {
  if(!source){ return; }
  try{
    source.stop();
  }catch(e){}
  source.onended = null;
  source.disconnect(0);
  try{source.buffer = getScratchBuffer();}catch(e){}
}

/**
 * Since soundtouch time is incorrect, use TimeNode to determine how long soundtouch takes to start playing audio.
 * @param audioBuffer
 * @param context
 * @param playbackRate
 */
export async function determineAmountOfTimeItTakesForAudioToStart(audioBuffer: AudioBuffer, context: AudioContext, playbackRate: number): Promise<number> {
  audioBuffer = createBufferFullOfSound(context, audioBuffer.sampleRate);
  const pitchShifter = new PitchShifter(context, audioBuffer);
  pitchShifter.tempo = playbackRate; //unsure if this matters yet.
  const timeNode = new TimeNode(context);
  const gain = context.createGain();
  gain.gain.value = 0;
  pitchShifter.connect(timeNode.node);
  timeNode.connect(gain);
  gain.connect(context.destination);
  const time = await timeNode.timeUntilDataPromise;
  pitchShifter.disconnect();
  timeNode.disconnect();
  gain.disconnect();
  logger.lessonAudio.debug({message: `It takes SoundTouch ${time}ms to start playing`});
  return time;
}

function createBufferFullOfSound(context: AudioContext, sampleRate: number): AudioBuffer {
  const audioBuffer = context.createBuffer(2, sampleRate, sampleRate);
  const channelData = audioBuffer.getChannelData(0);
  for(let i = 0; i < audioBuffer.length; ++i){
    channelData[i] = 1;
  }
  return audioBuffer;
}

/**
 * SoundTouch's timePlayed property is incorrect (https://github.com/cutterbl/SoundTouchJS/issues/17), and audio playback does not immediately start when you connect pitchshift, which means we can't start things in sync.
 * To get around this issue, we can connect the pitchshifter to a script processor node that evaluates when audio data starts to come through, and calculate how long it took to come through.
 */
class TimeNode {
  node: ScriptProcessorNode;
  processCount = 0;
  intervalId = 0;
  currentTimeMs = 0;
  audioContext: AudioContext;
  lastAudioProcessTime: number = 0;
  connectTime: number;
  timeUntilData: number | undefined;
  timeUntilDataPromise: Promise<number>;
  promiseResolve: (timeUntilData: number) => void;
  bufferSize = 256;
  constructor(audioContext: AudioContext){
    this.node = audioContext.createScriptProcessor(this.bufferSize, 2, 2);
    this.node.onaudioprocess = this.onAudioProcess.bind(this);
    this.audioContext = audioContext;
    this.timeUntilDataPromise = new Promise((resolve)=>{
      this.promiseResolve = resolve;
    });
  }
  connect(destination: AudioNode){
    this.node.connect(destination);
    this.lastAudioProcessTime = this.audioContext.currentTime;
    this.connectTime = this.audioContext.currentTime;
  }
  disconnect(){
    this.node.disconnect();
    window.clearInterval(this.intervalId);
    this.processCount = 0;
    this.timeUntilData = undefined;
  }
  getCurrentTime(){
    const msSinceLastAudioProcess = (this.audioContext.currentTime - this.lastAudioProcessTime) * 1000;
    return this.currentTimeMs + msSinceLastAudioProcess;
  }
  onAudioProcess(event: AudioProcessingEvent){
    ++this.processCount;
    const inputBuffer = event.inputBuffer;
    const sampleRate = inputBuffer.sampleRate;
    const sampleSize = inputBuffer.length;
    const outputBuffer = event.outputBuffer;
    const timeMs = 1000 / (sampleRate / sampleSize);
    this.currentTimeMs += timeMs;

    const inputChannelData = inputBuffer.getChannelData(0);
    // console.log(`mark: ${this.mark}`, inputChannelData);
    const indexAtWhichChannelDataIsNotEmpty = findIndexAtWhichChannelDataIsNotEmpty(inputChannelData);
    if(this.timeUntilData === undefined && indexAtWhichChannelDataIsNotEmpty > -1){
      // this.timeUntilData = ((this.audioContext.currentTime - this.connectTime) * 1000) - (timeMs * 6);  <-- still just a tad bit fast.
      // const timeUntilIndex = (indexAtWhichChannelDataIsNotEmpty / inputBuffer.length) * timeMs;  <-- probably not needed.
      this.timeUntilData = this.currentTimeMs - timeMs;
      this.promiseResolve(this.timeUntilData);
    }

    for (let channel = 0; channel < outputBuffer.numberOfChannels; channel++) {
      // cloneBuffer.copyToChannel(buffer.getChannelData(channel), channel); < -- no iOS support
      const inputChannelData = inputBuffer.getChannelData(channel);
      outputBuffer.getChannelData(channel).set(inputChannelData);
    }

    this.lastAudioProcessTime = this.audioContext.currentTime;
  }
}

function findIndexAtWhichChannelDataIsNotEmpty(channelData: Float32Array){
  for(let i = 0; i < channelData.length; ++i){
    if(channelData[i] !== 0){
      return i;
    }
  }
  return -1;
}